import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useState } from "react";
import dayjs from "dayjs";
import { createParticipant } from "../../api/user";
import { useToast } from "../../components/toaster";

const ParticipantAddForm = ({
  isParticipantFormOpen,
  setIsParticipantFormOpen,
  setUserAdded,
}) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    teamMemberCount: 1,
    registrationDate: dayjs(),
    bookingSource: "DIRECT", // NEW FIELD
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { showToast } = useToast();

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    if (formData.teamMemberCount < 1)
      newErrors.teamMemberCount = "Must be at least 1 participant";

    if (
      !formData.registrationDate ||
      formData.registrationDate.isBefore(dayjs(), "day")
    ) {
      newErrors.registrationDate = "Date cannot be in the past";
    }

    if (!formData.bookingSource) {
      newErrors.bookingSource = "Booking source is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear email error when user starts typing
    if (name === "email" && errors.email) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.email;
        return newErrors;
      });
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, registrationDate: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      setError(null);
      const formattedNewDate = dayjs(formData.registrationDate).format("YYYY-MM-DD");
      const formatedData = {
        ...formData,
        registrationDate: new Date(formattedNewDate),
      }
      const response = await createParticipant(formatedData);

      if (response.data) {
        showToast(response.data.message, "success");
        setFormData({
          fullName: "",
          email: "",
          teamMemberCount: 1,
          registrationDate: dayjs(),
          bookingSource: "DIRECT", // reset to default
        });
        setIsParticipantFormOpen(false);
        setUserAdded(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add participant");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: "",
      email: "",
      teamMemberCount: 1,
      registrationDate: dayjs(),
      bookingSource: "DIRECT", // reset
    });
    setIsParticipantFormOpen(false);
  }

  return (
    <Dialog
      open={isParticipantFormOpen}
      onClose={() => !isSubmitting && setIsParticipantFormOpen(false)}
      maxWidth="sm"
      fullWidth
      sx={{ borderRadius: 2 }}
    >
      <DialogTitle>
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{
            background: 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
            color: 'white',
            padding: 2,
            borderRadius: 2,
            textAlign: { xs: 'center', md: 'left' }
          }}
        >
          Add New Participant
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            margin="normal"
            label="Full Name"
            name="fullName"
            variant="outlined"
            value={formData.fullName}
            onChange={handleChange}
            error={!!errors.fullName}
            helperText={errors.fullName}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Email Address"
            name="email"
            type="email"
            variant="outlined"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            placeholder="Enter Email Address"
          />

          <DatePicker
            label="Registration Date"
            value={formData.registrationDate}
            onChange={handleDateChange}
            format="DD/MM/YYYY"
            minDate={dayjs()}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "normal",
                error: !!errors.registrationDate,
                helperText: errors.registrationDate,
              },
            }}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Number of Participants"
            name="teamMemberCount"
            type="number"
            variant="outlined"
            value={formData.teamMemberCount}
            onChange={handleChange}
            inputProps={{ min: 1 }}
            error={!!errors.teamMemberCount}
            helperText={errors.teamMemberCount}
          />

          {/* NEW FIELD */}
          <FormControl fullWidth margin="normal" error={!!errors.bookingSource}>
            <InputLabel>Booking Source</InputLabel>
            <Select
              name="bookingSource"
              value={formData.bookingSource}
              onChange={handleChange}
              label="Booking Source"
            >
              <MenuItem value="DIRECT">Direct</MenuItem>
              <MenuItem value="PARTNER">Partner</MenuItem>
            </Select>
            {errors.bookingSource && (
              <Typography color="error" variant="caption">
                {errors.bookingSource}
              </Typography>
            )}
          </FormControl>

          <DialogActions sx={{ px: 0, pb: 0 }}>
            <Button
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              endIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ParticipantAddForm;